import { useCallback, useMemo } from "react";
import type { ThreadStateMessage } from "../../../api/threads/liveMessages";
import { withFacts, type ThreadOverview } from "../../../api/threads/threadDecoders";
import { readThread } from "../../../api/threads/threadsApi";
import { useLoad, type LoadHandle } from "../../../shared/loading/useLoad";
import type { LiveThreadSnapshot } from "../live/threadLiveClient";

/**
 * The Thread's overview (`GET /threads/{id}`) with what it says now: the overview read and
 * the live state carry the same facts with module C's revision (W-22), and the newer of the
 * two is current, so nothing is read again for an entry.
 */
export function useThreadOverview(threadId: string, live: LiveThreadSnapshot | undefined): LoadHandle<ThreadOverview> {
  const load = useCallback((signal: AbortSignal) => readThread(threadId, signal), [threadId]);
  const handle = useLoad(load);
  const liveState = live?.state ?? null;
  const state = useMemo(
    () => (handle.state.status === "loaded" ? { status: "loaded" as const, value: currentOverview(handle.state.value, liveState) } : handle.state),
    [handle.state, liveState],
  );
  return { ...handle, state };
}

/** The overview with the newer facts by revision; on a tie the live state's, which came later. Pure. */
export function currentOverview(overview: ThreadOverview, live: ThreadStateMessage | null): ThreadOverview {
  return live === null || live.facts.projection.revision < overview.projection.revision ? overview : withFacts(overview, live.facts, live.viewer);
}
